/**
 * Per-workstream top border lines (MapWorkstreamBackground.vue): a 1px gray rule
 * along the top of each visible workstream band.
 *
 * This is screen-space chrome, NOT map content: in the SVG the line is
 * `x1=0 → x2=workstreamLineLength` with only its Y panned (`workstreamY`), so it
 * spans the viewport from the left edge and does NOT scroll horizontally — like
 * the band fills (BandsPainter). It must therefore start at screen x=0, never at
 * `screenX(0)` (the map-origin column), which scrolls far off-screen-left as you
 * zoom in — there the fixed-length line lands entirely outside the viewport (the
 * line "stops short" / vanishes) and the huge negative coordinate strokes
 * unreliably (the "flaky" look). The sidebar overlay re-covers the left strip.
 */
import { cssVar } from '../../draw/palette';
import type { SceneContext } from '../../engine/SceneContext';

export function drawWorkstreamBorders(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  ctx.strokeStyle = cssVar('--color-gray-300', '#D0D5DD');
  ctx.lineWidth = 1;
  // SVG length is a fixed 4000 (`workstreamLineLength`); span at least the full
  // viewport so it always reaches the right edge on wide screens too.
  const lineLength = Math.max(map.workstreamLineLength, scene.width);
  for (const ws of io.workstreams) {
    if (ws.isHiddenOnMap || map.filterHiddenWorkstreamIds.has(ws.id)) {
      continue;
    }
    const y = map.workstreamY(ws.id) + 0.5;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(lineLength, y);
    ctx.stroke();
  }
}
